import { Pencil, Check, AlertTriangle, Trash2, Plus, X, AlertCircle, CheckCircle2 } from 'lucide-react';
import { ProductDesignPageHeader, ProductDesignSection } from './ProductDesignDocPrimitives';
import { DataTable, Callout, type Column } from '../../components/ds';
import {
  MEASURED_ON,
  FILES_IMPORTING_LUCIDE,
  DISTINCT_ICONS,
  OFF_RAMP_SIZE_USES,
  SIZE_RAMP,
  CONCEPT_USAGE,
  type ConceptUsage,
} from './designUsage.generated';

/**
 * Icon library — the spec that did not exist.
 *
 * This page used to be three rules of thumb about stroke width. Meanwhile the
 * app had grown 371 distinct lucide icons across 1,169 files with no ruling on
 * which glyph means what — five different icons for "edit", and a near-even
 * split between AlertTriangle and AlertCircle for "warning".
 *
 * The counts are generated (scripts/buildIconUsage.ts) rather than transcribed,
 * because a hand-typed count is wrong the day after it is written. The
 * canonical choices are curated — only a person can rule that Pencil and Edit2
 * mean the same thing.
 */

const SIZE_ROWS = [
  { name: 'xs', cls: 'h-3 w-3', px: '12px', use: 'Inline with mono text at 0.72rem — a sort caret, a chevron inside a chip.' },
  { name: 'sm', cls: 'h-3.5 w-3.5', px: '14px', use: 'The dense default: buttons, tabs, table cells, disclosure chevrons.' },
  { name: 'md', cls: 'h-4 w-4', px: '16px', use: 'The general default. Body-text-adjacent icons, nav items, toolbar controls.' },
  { name: 'lg', cls: 'h-5 w-5', px: '20px', use: 'Page-header actions and anything that must stay tappable on touch.' },
  { name: 'xl', cls: 'h-6 w-6', px: '24px', use: 'Feature affordances and section markers.' },
  { name: '2xl', cls: 'h-8 w-8', px: '32px', use: 'Empty states and standalone illustration. Rare by design.' },
];

const CONCEPT_COLUMNS: Column<ConceptUsage>[] = [
  { key: 'concept', header: 'Concept', width: '18%' },
  {
    key: 'canonical',
    header: 'Use this',
    width: '14%',
    render: (r) => <span className="font-mono text-[var(--green)] dark:text-[var(--text-ter)]">{r.canonical}</span>,
  },
  {
    key: 'usage',
    header: 'In the tree today',
    width: '30%',
    render: (r) => (
      <span className="flex flex-wrap gap-x-3 gap-y-1">
        {r.usage.map((u) => (
          <span
            key={u.name}
            className={`font-mono text-[13px] ${
              u.name === r.canonical ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--coral)]'
            }`}
          >
            {u.name} <span className="tabular-nums text-[var(--text-dim)]">{u.count}</span>
          </span>
        ))}
      </span>
    ),
  },
  { key: 'why', header: 'Why' },
];

export const PrioritiesAIIconLibraryPage: React.FC = () => {
  const rampTotal = SIZE_RAMP.reduce((n, s) => n + s.count, 0);
  return (
    <>
      <ProductDesignPageHeader
        title="Icon library"
        eyebrow="Iconography · one glyph per meaning"
        subtitle={
          <>
            Lucide is the icon set, and the question that matters is not which icons exist — it is
            which one means what. The app carries{' '}
            <strong className="text-[var(--text-pri)]">{DISTINCT_ICONS}</strong> distinct icons
            across <strong className="text-[var(--text-pri)]">{FILES_IMPORTING_LUCIDE}</strong>{' '}
            files, and until now nothing ruled on that. Counts below are generated from a scan of{' '}
            <code className="font-mono text-[0.85em] text-[var(--mustard)]">src/</code>, measured{' '}
            {MEASURED_ON}.
          </>
        }
      />

      <ProductDesignSection
        title="One glyph per meaning"
        hint="Where the tree currently uses more than one icon for a single concept. Green is the ruling; coral is drift to migrate opportunistically."
      >
        <Callout tone="coral" label="The cost" title="Five different icons mean “edit”">
          When one concept has several glyphs, users stop reading icons as language and fall back to
          reading every label — which is the entire benefit of an icon, gone. The worst case here is{' '}
          <code className="font-mono text-[0.85em]">X</code> used for delete: it collides with “close
          this panel”, so destroy and dismiss look identical.
        </Callout>

        <div className="mt-5">
          <DataTable
            columns={CONCEPT_COLUMNS}
            rows={CONCEPT_USAGE}
            getRowKey={(r) => r.concept}
            density="comfortable"
            caption="Canonical icon per concept"
            captionHidden
          />
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-x-8 gap-y-4">
          {[
            { Icon: Pencil, label: 'Edit', tone: 'green' },
            { Icon: Check, label: 'Confirmed', tone: 'green' },
            { Icon: CheckCircle2, label: 'Success state', tone: 'green' },
            { Icon: AlertTriangle, label: 'Warning', tone: 'green' },
            { Icon: AlertCircle, label: 'Information', tone: 'green' },
            { Icon: Trash2, label: 'Delete', tone: 'green' },
            { Icon: Plus, label: 'Add', tone: 'green' },
            { Icon: X, label: 'Close / dismiss — never delete', tone: 'coral' },
          ].map(({ Icon, label, tone }) => (
            <span key={label} className="inline-flex items-center gap-2">
              <Icon className={`h-4 w-4 text-[var(--${tone})]`} />
              <span className="font-mono text-[13px] uppercase tracking-[0.1em] text-[var(--text-ter)]">
                {label}
              </span>
            </span>
          ))}
        </div>
      </ProductDesignSection>

      <ProductDesignSection
        title="Size ramp"
        hint="Six steps, named. These are not invented — they are the sizes the app already converged on, which is why they are the ones to keep."
      >
        <DataTable
          columns={[
            { key: 'name', header: 'Name', width: '10%', render: (r: typeof SIZE_ROWS[number]) => (
              <span className="font-mono text-[var(--text-pri)]">{r.name}</span>
            ) },
            { key: 'cls', header: 'Class', width: '16%', render: (r: typeof SIZE_ROWS[number]) => (
              <span className="font-mono text-[13px] text-[var(--mustard)]">{r.cls}</span>
            ) },
            { key: 'px', header: 'Size', width: '10%' },
            {
              key: 'count',
              header: 'Uses',
              width: '10%',
              numeric: true,
              render: (r: typeof SIZE_ROWS[number]) =>
                SIZE_RAMP.find((s) => s.cls === r.cls)?.count.toLocaleString() ?? '—',
            },
            { key: 'use', header: 'What it is for' },
          ]}
          rows={SIZE_ROWS}
          getRowKey={(r) => r.name}
          density="compact"
          caption="Icon size ramp"
          captionHidden
          footer={
            <span className="font-mono text-xs uppercase tracking-[0.12em]">
              {rampTotal.toLocaleString()} on-ramp ·{' '}
              <span className="text-[var(--coral)]">{OFF_RAMP_SIZE_USES.toLocaleString()} off-ramp</span>
            </span>
          }
        />
        <p className="mt-4 text-sm leading-relaxed text-[var(--text-sec)]">
          The off-ramp count is uses of <code className="font-mono text-[0.85em]">h-7</code>,{' '}
          <code className="font-mono text-[0.85em]">h-9</code>,{' '}
          <code className="font-mono text-[0.85em]">h-10</code>,{' '}
          <code className="font-mono text-[0.85em]">h-12</code>,{' '}
          <code className="font-mono text-[0.85em]">h-2.5</code> and{' '}
          <code className="font-mono text-[0.85em]">h-1.5</code> — sizes nothing on this page calls
          for. Most are one-off nudges to make an icon line up with a neighbour, which is the symptom
          of a control height chosen off-system rather than of a missing icon size.
        </p>
      </ProductDesignSection>

      <ProductDesignSection
        title="Rules"
        hint="Short, because the two decisions above carry almost all of the weight."
      >
        <ul className="flex flex-col gap-3 text-sm leading-relaxed text-[var(--text-sec)]">
          <li>
            <strong className="text-[var(--text-pri)]">Default stroke.</strong> Leave lucide's stroke
            at its default. It was drawn to sit beside text at 14–16px; thickening it to match a
            heavier heading makes icons read as illustration.
          </li>
          <li>
            <strong className="text-[var(--text-pri)]">Colour comes from the token, not the icon.</strong>{' '}
            An icon inherits the tone of what it labels — <code className="font-mono text-[0.85em]">var(--coral)</code>{' '}
            for destructive, <code className="font-mono text-[0.85em]">var(--text-ter)</code> when it
            is decoration beside a label. Never a hex.
          </li>
          <li>
            <strong className="text-[var(--text-pri)]">An icon alone needs a name.</strong> An
            icon-only control takes an <code className="font-mono text-[0.85em]">aria-label</code>,
            and if the meaning is not obvious it takes a{' '}
            <code className="font-mono text-[0.85em]">Tooltip</code> too — not a native{' '}
            <code className="font-mono text-[0.85em]">title</code>, which never appears on touch.
          </li>
          <li>
            <strong className="text-[var(--text-pri)]">Icon buttons come from{' '}
            <code className="font-mono text-[0.85em]">mono/iconButton</code>.</strong> That module
            exists because the same ~30-class string had been hand-copied eleven ways across seven
            files.
          </li>
          <li>
            <strong className="text-[var(--text-pri)]">Adding to the {DISTINCT_ICONS} is a real
            decision.</strong> Check whether one of the concepts above already covers it. A new glyph
            for an existing meaning is the drift this page exists to stop.
          </li>
        </ul>
      </ProductDesignSection>
    </>
  );
};
